// API Client for DIOS10 Frontend
// This file handles all HTTP requests with error handling, authentication, and response parsing

import { API_ENDPOINTS, HTTP_METHODS, REQUEST_TIMEOUT, DEFAULT_HEADERS, STATUS_CODES, ERROR_MESSAGES } from './config';
import { getStoredLanguage } from '../utils/locale';
import logger from '../utils/logger';

class ApiClient {
    constructor() {
        this.baseURL = API_ENDPOINTS.BASE;
        this.timeout = REQUEST_TIMEOUT;
    }

    // Get authentication token from localStorage or cookies
    getAuthToken() {
        // Check localStorage first (for mobile apps)
        const token = localStorage.getItem('authToken');
        if (token) {
            return token;
        }
        
        // If no token in localStorage, cookies will be sent automatically
        return null;
    }

    // Build headers for requests
    buildHeaders(customHeaders = {}, isAdminRequest = false) {
        const headers = { ...DEFAULT_HEADERS, ...customHeaders };
        headers['Accept-Language'] = getStoredLanguage();
        
        // Use custom Authorization if provided (e.g. wallet deposit as linked player); otherwise use stored token
        const token = headers.Authorization ? null : this.getAuthToken();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        
        return headers;
    }

    // Handle API responses
    async handleResponse(response, requestUrl) {
        const contentType = response.headers.get('content-type');
        let data;

        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
        } else {
            data = await response.text();
        }

        if (!response.ok) {
            // Backend can return error in different formats: {error: "..."}, {message: "..."}, or {errorMessage: "..."}
            const errorMessage = data.error || data.message || data.errorMessage || ERROR_MESSAGES.SERVER_ERROR;
            const error = new Error(errorMessage);
            error.status = response.status;
            error.data = data;
            error.url = requestUrl;
            throw error;
        }

        return data;
    }

    // Handle different types of errors
    handleError(error) {
        // Handle network errors (Failed to fetch, NetworkError, etc.)
        if (error.message === 'Failed to fetch' || 
            error.message.includes('NetworkError') || 
            error.message.includes('fetch') ||
            error.name === 'TypeError' && error.message.includes('fetch')) {
            const networkError = new Error('Unable to connect to server. Please check if the backend is running and accessible.');
            networkError.isNetworkError = true;
            throw networkError;
        }

        if (error.name === 'AbortError') {
            const timeoutError = new Error(ERROR_MESSAGES.TIMEOUT_ERROR);
            throw timeoutError;
        }

        if (!navigator.onLine) {
            const networkError = new Error(ERROR_MESSAGES.NETWORK_ERROR);
            throw networkError;
        }

        // Preserve the original error properties
        const newError = new Error(error.message || ERROR_MESSAGES.SERVER_ERROR);
        newError.status = error.status;
        newError.data = error.data;
        newError.response = error.response;
        newError.url = error.url;

        switch (error.status) {
            case STATUS_CODES.UNAUTHORIZED:
                // Don't logout on 401 from wallet or admin API: admin may get 401 on wallet if no linked user; admin section must not kick out on 401
                const url401 = newError.url || '';
                const isWalletOrAdmin401 = url401.includes('/api/wallet/') || (url401.includes('/api/admin/') && !url401.includes('/api/admin/auth/login'));
                if (!isWalletOrAdmin401) {
                    localStorage.removeItem('authToken');
                    window.location.href = '/';
                }
                newError.message = ERROR_MESSAGES.UNAUTHORIZED;
                break;
            
            case STATUS_CODES.FORBIDDEN:
                newError.message = error.data?.error || error.message || ERROR_MESSAGES.UNAUTHORIZED;
                break;
            
            case STATUS_CODES.NOT_FOUND:
                if ((newError.data?.error || '').toLowerCase().includes('user not found') ||
                    (newError.message || '').toLowerCase().includes('user not found')) {
                    // Do not logout on 404 from wallet (e.g. balance) to avoid redirect when opening /deposit
                    const isWalletRequest = (newError.url || '').includes('/api/wallet/');
                    if (!isWalletRequest) {
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('user');
                        window.location.href = '/login';
                        newError.message = 'User not found. Please login again.';
                    } else {
                        newError.message = newError.message || ERROR_MESSAGES.NOT_FOUND;
                    }
                } else {
                    newError.message = ERROR_MESSAGES.NOT_FOUND;
                }
                break;
            
            case STATUS_CODES.TOO_MANY_REQUESTS:
                newError.message = ERROR_MESSAGES.TOO_MANY_REQUESTS;
                break;
            
            case STATUS_CODES.UNPROCESSABLE_ENTITY:
                newError.message = error.data?.message || ERROR_MESSAGES.VALIDATION_ERROR;
                break;
            
            default:
                // Keep the original error message for unknown status codes
                newError.message = error.message || ERROR_MESSAGES.SERVER_ERROR;
                break;
        }

        throw newError;
    }

    // Generic request method
    async request(url, options = {}) {
        const controller = new AbortController();
        const timeoutMs = options.timeout != null ? Number(options.timeout) : this.timeout;
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        // Check if this is an admin request
        const isAdminRequest = url.includes('/api/admin') && !url.includes('/api/admin/auth/login');

        // Handle query parameters
        let finalUrl = url;
        if (options.params && Object.keys(options.params).length > 0) {
            const params = new URLSearchParams();
            Object.keys(options.params).forEach(key => {
                if (options.params[key] !== null && options.params[key] !== undefined && options.params[key] !== '') {
                    params.append(key, options.params[key]);
                }
            });
            const queryString = params.toString();
            if (queryString) {
                finalUrl = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
            }
        }

        try {
            const headers = this.buildHeaders(options.headers, isAdminRequest);
            
            // Debug logging for admin requests
            if (isAdminRequest) {
                logger.debug('Admin request', {
                    url: finalUrl,
                    method: options.method || HTTP_METHODS.GET,
                    hasToken: !!headers['x-admin-token']
                });
            }

            const config = {
                method: options.method || HTTP_METHODS.GET,
                headers: headers,
                credentials: 'include', // Include cookies
                signal: controller.signal
            };

            // Don't overwrite headers with only custom keys (e.g. Authorization) - merge so Content-Type stays. timeout is not a fetch option.
            const { params, headers: customHeaders, body: bodyOption, timeout: _timeoutOpt, ...restOptions } = options;
            Object.assign(config, restOptions);
            if (customHeaders && typeof customHeaders === 'object') {
                config.headers = { ...config.headers, ...customHeaders };
            }

            if (bodyOption != null && config.method !== HTTP_METHODS.GET) {
                config.body = typeof bodyOption === 'object' && !(bodyOption instanceof FormData)
                    ? JSON.stringify(bodyOption)
                    : bodyOption;
            }

            const response = await fetch(finalUrl, config);
            clearTimeout(timeoutId);
            
            const data = await this.handleResponse(response, finalUrl);
            
            // Debug logging for admin responses
            if (isAdminRequest) {
                logger.debug('Admin response', {
                    url: finalUrl,
                    status: response.status
                });
            }
            
            return data;
        } catch (error) {
            clearTimeout(timeoutId);
            this.handleError(error);
        }
    }

    // HTTP method shortcuts
    async get(url, options = {}) {
        return this.request(url, { ...options, method: HTTP_METHODS.GET });
    }

    async post(url, body, options = {}) {
        return this.request(url, { ...options, method: HTTP_METHODS.POST, body });
    }

    async put(url, body, options = {}) {
        return this.request(url, { ...options, method: HTTP_METHODS.PUT, body });
    }

    async delete(url, options = {}) {
        return this.request(url, { ...options, method: HTTP_METHODS.DELETE });
    }

    async patch(url, body, options = {}) {
        return this.request(url, { ...options, method: HTTP_METHODS.PATCH, body });
    }
}

// Create and export a singleton instance
const apiClient = new ApiClient();
export default apiClient;
